/**
 * Bouwt de lijst die het scherm tekent: lessen, met de pauzes en de NU-markering
 * op de juiste plek ertussen.
 *
 * Dit is bewust een pure functie in plaats van logica verspreid over de render van
 * een component. Twee redenen: de plaatsingsregels zijn het soort code waar een
 * randgeval in schuilt (een pauze na het laatste lesuur, "nu" vóór de eerste les of
 * ná de laatste), en zo is precies dat te testen zonder een DOM op te tuigen.
 *
 * Sinds het ontwerp waarin de status het hele kaartvlak kleurt, draagt een lópende
 * les de NU-markering zélf: een groene rand, het resterende aantal minuten en een
 * voortgangsbalk onderin. De losse NU-regel blijft alleen over voor de momenten
 * waarop er géén les loopt — een pauze, vóór het eerste uur, na het laatste. Zo
 * staat er nooit twee keer "u bent hier" op het scherm.
 */
import type { Les, Pauze } from '../types';
import { minuten } from './tijd';

/** De NU-staat van een les die op dit moment bezig is. */
export interface LesNu {
  /** Hele minuten tot het einde van de les; minimaal 1 zolang de les loopt. */
  restMinuten: number;
  /** Hoe ver de les is, als fractie tussen 0 en 1. */
  voortgang: number;
}

export type RoosterItem =
  | { soort: 'pauze'; sleutel: string; pauze: Pauze }
  | { soort: 'nu'; sleutel: string }
  | { soort: 'les'; sleutel: string; les: Les; nu: LesNu | null };

/**
 * Loopt deze les op dit moment? Zo ja: hoeveel minuten nog, en hoe ver is hij.
 *
 * De eindminuut telt niet mee. Sluiten twee uren op elkaar aan (09:20 eind, 09:20
 * start), dan is om 09:20 dus precies één van de twee bezig en niet allebei.
 */
export function lesNu(les: Les, nuMinuten: number | null): LesNu | null {
  if (nuMinuten === null) return null;

  const start = minuten(les.startTijd);
  const eind = minuten(les.eindTijd);
  if (!(nuMinuten >= start && nuMinuten < eind)) return null;

  const duur = eind - start;
  return {
    restMinuten: eind - nuMinuten,
    voortgang: duur > 0 ? (nuMinuten - start) / duur : 0,
  };
}

/**
 * Hoe ver de schooldag gevorderd is, als fractie tussen 0 en 1 — de vulling van
 * het tijdbalkje links van de lessen.
 *
 * Gemeten van de start van de eerste les tot het einde van de laatste, en niet van
 * middernacht: vóór het eerste uur hoort het balkje leeg te staan en na het laatste
 * vol, en niet respectievelijk een derde en driekwart.
 */
export function dagVoortgang(lessen: Les[], nuMinuten: number | null): number | null {
  if (nuMinuten === null || lessen.length === 0) return null;

  const start = Math.min(...lessen.map((l) => minuten(l.startTijd)));
  const eind = Math.max(...lessen.map((l) => minuten(l.eindTijd)));
  if (eind <= start) return null;

  return Math.min(1, Math.max(0, (nuMinuten - start) / (eind - start)));
}

/**
 * @param lessen chronologisch gesorteerd door de backend
 * @param pauzes pauzestructuur van de school; `na` = na welk lesuur
 * @param nuMinuten minuten sinds middernacht, van de klok op de tablet, of null
 *        als de getoonde dag niet vandaag is — dan hoort er geen NU-markering in
 *        de lijst te staan
 */
export function bouwRoosterItems(
  lessen: Les[],
  pauzes: Pauze[],
  nuMinuten: number | null,
): RoosterItem[] {
  const items: RoosterItem[] = [];
  const nogTePlaatsen = [...pauzes];
  // Loopt er nu een les, dan draagt díe kaart de markering en hoort de losse
  // NU-regel er helemaal niet in te staan.
  const erLooptEenLes = lessen.some((les) => lesNu(les, nuMinuten) !== null);
  let nuGeplaatst = erLooptEenLes;

  for (const les of lessen) {
    // Een pauze hoort vóór het lesuur dat erna komt. Zodra we dat uur bereiken
    // plaatsen we hem, en halen hem uit de lijst zodat hij niet twee keer
    // verschijnt als twee lessen hetzelfde uur delen.
    const index = nogTePlaatsen.findIndex((p) => p.na != null && les.uur === p.na + 1);
    if (index !== -1) {
      const [pauze] = nogTePlaatsen.splice(index, 1);
      if (pauze) items.push({ soort: 'pauze', sleutel: `pauze-${pauze.naam}-${les.id}`, pauze });
    }

    // De NU-markering komt vóór de eerste les die nog niet begonnen is — dus in het
    // gat waar we op dat moment in zitten: een pauze, of de tijd vóór het eerste uur.
    if (!nuGeplaatst && nuMinuten !== null && minuten(les.startTijd) > nuMinuten) {
      items.push({ soort: 'nu', sleutel: `nu-voor-${les.id}` });
      nuGeplaatst = true;
    }

    items.push({ soort: 'les', sleutel: `les-${les.id}`, les, nu: lesNu(les, nuMinuten) });
  }

  // Geen volgende les meer om de markering vóór te zetten, en er loopt er ook geen:
  // de dag is voorbij, dus hoort hij onderaan.
  if (nuMinuten !== null && lessen.length > 0 && !nuGeplaatst) {
    items.push({ soort: 'nu', sleutel: 'nu-na-laatste' });
  }

  return items;
}
